import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';

const SOCKET_NOTIFICATIONS_BASE_URL = `${process.env.REACT_APP_SOCKET_NOTIFICATIONS_BASE_URL}`;

export type EventsEnums =
  | 'connect'
  | 'conversation'
  | 'user'
  | 'message'
  | 'error'
  | 'info'
  | 'join'
  | 'leave'
  | 'active-room'
  | 'user-status'
  | 'unreadNotifications'
  | 'notification';

export const useNotificationsSocked = ({ token }) => {
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const { current } = useRef<any>({ socket: null });
  const eventHandlers = useRef<Record<string, any>>({});

  useEffect(() => {
    if (!token && typeof token !== 'string') {
      return;
    }

    current.socket = io(SOCKET_NOTIFICATIONS_BASE_URL, {
      transports: ['websocket'],
      query: { token },
    });

    current.socket.on('connect', () => {
      setIsSocketConnected(!!current.socket?.connected);
    });

    // eslint-disable-next-line consistent-return
    return () => {
      current.socket?.off('connect');
      current.socket?.close();
      current.socket?.on('disconnect', () => {
        setIsSocketConnected(false);
      });
    };
  }, [token]);

  const emitEvent = useCallback((event: EventsEnums, data: any) => {
    current.socket?.emit(event, data);
  }, []);

  const subscribeToEvents = useCallback((event: EventsEnums, cb: (...args: any) => void) => {
    eventHandlers.current[event] = cb;
    current.socket?.on(event, cb);
  }, []);

  const unsubscribeToEvents = useCallback((event: EventsEnums) => {
    if (eventHandlers.current[event]) {
      current.socket?.off(event, eventHandlers.current[event]);
      delete eventHandlers.current[event];
    }
  }, []);

  return {
    isSocketConnected,
    socket: current.socket,
    emitEvent,
    subscribeToEvents,
    unsubscribeToEvents,
  };
};
